<!-- eslint-disable @intlify/vue-i18n/no-dynamic-keys -->
<script setup lang="ts">
import type { EmbedSettingsControls } from "@/composables/useEmbedSettings";
import CodeEditor from "@/components/common/CodeEditor.vue";
import { useAppStore } from "@/store/app";
import {
  buildEmbedCopyArtifacts,
  EMBED_LAYOUT_TYPES,
  type EmbedLayoutId,
} from "@/util/embedCode";
import { emitPendoEvent } from "@/util/pendoConfig";
import { Copy01Icon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UIButton,
  UIModal,
  UIModalContent,
  UIModalHeader,
} from "@gohighlevel/ghl-ui";
import "codemirror/mode/htmlmixed/htmlmixed.js";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = withDefaults(
  defineProps<{
    controls: EmbedSettingsControls;
    widgetId: string;
    show: boolean;
    standardOnly?: boolean;
    basicEmbedCode?: string;
  }>(),
  {
    standardOnly: false,
    basicEmbedCode: "",
  }
);
const emit = defineEmits<{ (event: "update:show", value: boolean): void }>();
const store = useAppStore();
const { t } = useI18n();
const visible = computed({
  get: () => props.show,
  set: (show: boolean) => emit("update:show", show),
});
const layoutNames = Object.fromEntries(
  EMBED_LAYOUT_TYPES.map(({ id, legacyLabelKey }) => [id, t(legacyLabelKey)])
) as Record<EmbedLayoutId, string>;
const displayedCode = computed(() =>
  props.standardOnly ? props.basicEmbedCode : props.controls.embedCode.value
);
const editorOptions = {
  lineNumbers: true,
  lineWrapping: true,
  mode: "text/html",
  readOnly: "nocursor",
  theme: "default",
};
const copyCode = () => {
  if (props.standardOnly) {
    store.copyToClipBoard(displayedCode.value);
    return;
  }
  if (!props.controls.isValid.value) return;
  const stored = props.controls.toStoredSettings();
  const artifacts = buildEmbedCopyArtifacts({
    widgetId: props.widgetId,
    settings: stored,
    layoutName: layoutNames[props.controls.settings.layout.id],
  });
  store.integrate.formLayoutSettings = stored;
  store.saveToLocalStorage(artifacts.storageKey, artifacts.storageValue);
  store.copyToClipBoard(props.controls.embedCode.value);
  emitPendoEvent(
    "Copy-form-embed-code",
    artifacts.analytics as unknown as Record<string, string>
  );
};
</script>

<template>
  <UIModal v-model:show="visible" class-name="embed-code-dialog">
    <template #header>
      <UIModalHeader
        id="embed-code-modal"
        :title="t('integrate.embed.modalTitle')"
        @close="visible = false"
      />
    </template>
    <UIModalContent>
      <div class="embed-code-modal">
        <ol class="embed-code-modal__steps">
          <li class="embed-code-modal__step--code">
            <strong>{{ t("integrate.embed.copyStep") }}</strong>
            <div
              data-testid="embed-code-value"
              class="embed-code-modal__editor"
            >
              <CodeEditor
                :key="displayedCode"
                :code-input="displayedCode"
                :height="280"
                :editor-options="editorOptions"
              />
            </div>
            <UIButton
              id="copy-embed-code"
              data-testid="copy-embed-code"
              class="embed-code-modal__copy-button"
              type="primary"
              size="small"
              :disabled="!standardOnly && !controls.isValid.value"
              @click="copyCode"
            >
              <Copy01Icon class="embed-code-modal__copy-icon" />
              {{ t("integrate.embed.copyCode") }}
            </UIButton>
          </li>
          <li>
            <strong>{{ t("integrate.embed.openWebpageStep") }}</strong>
            <p>{{ t("integrate.embed.openWebpageStepHelp") }}</p>
          </li>
          <li>
            <strong>{{ t("integrate.embed.publishStep") }}</strong>
            <p>{{ t("integrate.embed.publishStepHelp") }}</p>
          </li>
        </ol>
      </div>
    </UIModalContent>
    <template #footer>
      <p class="embed-code-modal__footer">
        {{ t("integrate.embed.setupGuidePrompt") }}
        <a
          href="https://help.gohighlevel.com/support/solutions/articles/155000004524-embedding-highlevel-forms-on-non-highlevel-websites"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ t("integrate.embed.viewSetupGuides") }}
        </a>
      </p>
    </template>
  </UIModal>
</template>

<style scoped>
:global(.embed-code-dialog .hl-modal-content) {
  padding-top: 12px;
}
:global(.embed-code-dialog .n-card__footer) {
  padding-top: 16px !important;
}
.embed-code-modal__editor {
  --embed-code-gutter-width: 40px;

  overflow: hidden;
  border: 1px solid var(--gray-200, #eaecf0);
  border-radius: 8px;
  background: var(--gray-50, #f9fafb);
}
.embed-code-modal__steps {
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin: 4px 0 0;
  padding: 0;
  counter-reset: embed-step;
  list-style: none;
}
.embed-code-modal__steps li {
  position: relative;
  padding-left: 34px;
  counter-increment: embed-step;
}
.embed-code-modal__steps li::before {
  position: absolute;
  left: 0;
  display: grid;
  width: 24px;
  height: 24px;
  place-items: center;
  border-radius: 50%;
  background: var(--gray-100, #f2f4f7);
  content: counter(embed-step);
  font-size: 12px;
}
.embed-code-modal__steps p,
.embed-code-modal__footer {
  margin: 4px 0 0;
  color: var(--gray-600, #475467);
  font-size: 13px;
}
.embed-code-modal__footer a {
  color: var(--primary-600, #155eef);
  font-weight: 600;
  text-decoration: none;
}
.embed-code-modal__footer {
  width: 100%;
  margin: 0;
  padding-top: 0;
  border-top: 1px solid var(--gray-200, #eaecf0);
}
.embed-code-modal__step--code .embed-code-modal__editor {
  margin-block: 12px;
}
.embed-code-modal .embed-code-modal__editor :deep(.CodeMirror) {
  height: 280px !important;
  background: var(--gray-50, #f9fafb);
  color: var(--gray-700, #344054);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace !important;
  font-size: 13px !important;
  line-height: 20px !important;
}
.embed-code-modal .embed-code-modal__editor :deep(.CodeMirror-gutters),
.embed-code-modal .embed-code-modal__editor :deep(.CodeMirror-gutter) {
  width: var(--embed-code-gutter-width) !important;
}
.embed-code-modal .embed-code-modal__editor :deep(.CodeMirror-gutters) {
  position: absolute !important;
  left: 0 !important;
}
.embed-code-modal .embed-code-modal__editor :deep(.CodeMirror-sizer) {
  margin-left: var(--embed-code-gutter-width) !important;
}
.embed-code-modal .embed-code-modal__editor :deep(.CodeMirror-gutter-wrapper) {
  position: absolute !important;
  left: calc(-1 * var(--embed-code-gutter-width)) !important;
  width: var(--embed-code-gutter-width) !important;
}
.embed-code-modal .embed-code-modal__editor
  :deep(.CodeMirror-gutter-wrapper .CodeMirror-linenumber) {
  position: absolute !important;
  left: 0 !important;
  box-sizing: border-box !important;
  width: var(--embed-code-gutter-width) !important;
  min-width: var(--embed-code-gutter-width) !important;
  padding: 0 10px 0 4px !important;
  text-align: right !important;
}
.embed-code-modal .embed-code-modal__editor :deep(.CodeMirror-lines) {
  padding: 8px 0;
}
.embed-code-modal .embed-code-modal__editor :deep(.CodeMirror pre) {
  padding: 0 12px !important;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace !important;
  font-size: 13px !important;
  line-height: 20px !important;
  white-space: pre-wrap !important;
  word-break: normal !important;
  overflow-wrap: anywhere !important;
}
.embed-code-modal .embed-code-modal__editor :deep(.cm-s-default .cm-tag) {
  color: #c11574;
}
.embed-code-modal .embed-code-modal__editor
  :deep(.cm-s-default .cm-attribute) {
  color: #175cd3;
}
.embed-code-modal .embed-code-modal__editor :deep(.cm-s-default .cm-string) {
  color: #067647;
}
.embed-code-modal .embed-code-modal__editor :deep(.cm-s-default .cm-bracket) {
  color: var(--gray-500, #667085);
}
.embed-code-modal .embed-code-modal__editor :deep(.cm-s-default .cm-comment) {
  color: var(--gray-500, #667085);
}
.embed-code-modal__copy-icon {
  width: 16px;
  height: 16px;
}
.embed-code-modal__copy-button :deep(.n-button__content) {
  gap: 8px;
}
</style>
